const DEFAULT_HEADER_BANNER_BACKGROUND = '#CAFFEB'
const DEFAULT_HEADER_BANNER_TEXT_COLOR = '#363A45'
const DEFAULT_HEADER_BANNER_CTA_PRIMARY_TEXT_COLOR = '#FFFFFF'
const DEFAULT_HEADER_BANNER_KIND: App.LabHeaderBannerResult['kind'] = 'promotional-primary'
const PARAM_SEGMENT_PATTERN = /^:[A-Za-z0-9_-]+$/

export type TakeoverBannerLocation = 'search' | 'offer' | 'generic'

export const getTakeoverBannerLocation = (
  isSearchPage: boolean,
  isOfferPage: boolean,
): TakeoverBannerLocation => {
  if (isSearchPage) {
    return 'search'
  }
  if (isOfferPage) {
    return 'offer'
  }
  return 'generic'
}

const escapeRegExpSegment = (segment: string): string => {
  return segment.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&')
}

const patternToRegExp = (rawPattern: string): RegExp => {
  const pattern = rawPattern.trim()
  if (pattern.length === 0) {
    throw new Error('Pattern cannot be empty.')
  }
  if (!pattern.startsWith('/')) {
    throw new Error('Pattern must start with "/".')
  }
  if (pattern.startsWith('//')) {
    throw new Error('Pattern must not start with "//".')
  }

  const hasWildcardSuffix = pattern.endsWith('/*')
  if (pattern.includes('*') && !hasWildcardSuffix) {
    throw new Error('Wildcard is only supported as a trailing "/*" suffix.')
  }

  const basePath = hasWildcardSuffix ? pattern.slice(0, -2) : pattern
  if (basePath === '') {
    return /^\/(?:.*)?$/
  }

  const baseWithoutLeadingSlash = basePath.slice(1)
  if (baseWithoutLeadingSlash.length === 0) {
    return hasWildcardSuffix ? /^\/(?:.*)?$/ : /^\/$/
  }

  const segments = baseWithoutLeadingSlash.split('/')
  if (segments.some((segment) => segment.length === 0)) {
    throw new Error('Pattern cannot contain empty path segments.')
  }

  const regexSegments = segments.map((segment) => {
    if (segment.includes('*')) {
      throw new Error('Wildcard is only supported as a trailing "/*" suffix.')
    }
    if (segment.startsWith(':')) {
      if (!PARAM_SEGMENT_PATTERN.test(segment)) {
        throw new Error('Dynamic segments must use alphanumeric, underscore, or hyphen characters.')
      }
      return '[^/]+'
    }
    return escapeRegExpSegment(segment)
  })

  return new RegExp(`^/${regexSegments.join('/')}${hasWildcardSuffix ? '(?:/.*)?' : ''}$`)
}

export const isPathBlacklisted = (currentPath: string, patterns: Array<string>): boolean => {
  return patterns
    .map(pattern => pattern.trim())
    .filter(Boolean)
    .some((pattern) => {
      try {
        return patternToRegExp(pattern).test(currentPath)
      } catch {
        return false
      }
    })
}

export const getRenderableHeaderBanner = (
  headerBanners: App.LabHeaderBannersResult | undefined,
  currentPath: string,
  dismissedIds: Set<string>,
): App.LabHeaderBannerResult | undefined => {
  if (!headerBanners?.banners?.length) {
    return undefined
  }
  if (isPathBlacklisted(currentPath, headerBanners.globalRouteBlacklist)) {
    return undefined
  }

  const sorted = headerBanners.banners.slice().sort((a, b) => a.rank - b.rank)
  return sorted.find(banner => !dismissedIds.has(banner.id))
}

export const getHeaderBannerPresentation = (
  banner: App.LabHeaderBannerResult,
  isMobile: boolean,
): {
  kind: App.LabHeaderBannerResult['kind']
  isCustomAppearance: boolean
  description: string
  cta?: { label: string, path: string, style: 'primary' | 'secondary' | 'tertiary' }
  background: { mode: 'solid' | 'gradient', value: string } | { mode: 'image', imageId: string }
  textColor: string
  ctaPrimaryTextColor: string
  iconImageId?: string
} => {
  const description = isMobile && banner.description.mobile ? banner.description.mobile : banner.description.desktop
  const ctaItem = isMobile
    ? (banner.cta?.mobile ?? banner.cta?.desktop)
    : banner.cta?.desktop
  const cta = ctaItem
    ? {
        ...ctaItem,
        style: ctaItem.style ?? ((ctaItem as { outlined?: boolean }).outlined ? 'secondary' : 'tertiary'),
      }
    : undefined

  const textColor = banner.textColor || DEFAULT_HEADER_BANNER_TEXT_COLOR
  const ctaPrimaryTextColor = banner.ctaPrimaryTextColor || DEFAULT_HEADER_BANNER_CTA_PRIMARY_TEXT_COLOR

  const background = banner.background.mode === 'image'
    ? {
        mode: 'image' as const,
        imageId: banner.background.imageId,
      }
    : {
        mode: banner.background.mode,
        value: banner.background.value || DEFAULT_HEADER_BANNER_BACKGROUND,
      }

  return {
    kind: banner.kind || DEFAULT_HEADER_BANNER_KIND,
    isCustomAppearance: banner.isCustomAppearance ?? false,
    description,
    cta,
    background,
    textColor,
    ctaPrimaryTextColor,
    iconImageId: banner.iconImageId?.trim() || undefined,
  }
}
